import React, { useState, useEffect } from 'react'
import { DataGrid } from '@mui/x-data-grid'
import { Button, Paper, Stack } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import LeaveRegisterModal from './LeaveRegisterModal'
import { fetchLeave } from '../../../services/LeaveService'

const Status = {
    PENDING: 'PENDING',
    APPROVED: 'APPROVED',
    REJECTED: 'REJECTED'
}

const LeaveListLayout = () => {
    const navigate = useNavigate()
    const DEFAULT_PAGE_SIZE = 10
    const [dtr, setDTR] = useState([])
    const [loading, setLoading] = useState(false)
    const [submitting, setSubmitting] = useState(false) // New state for submission loading
    const [selectionModel, setSelectionModel] = useState([])
    const [searchTerm, setSearchTerm] = useState(null)
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
    const [totalCount, setTotalCount] = useState(0)
    const [refresh, setRefresh] = useState(false)
    const [responseMessage, setResponseMessage] = useState('')
    const [registerDialogOpen, setRegisterDialogOpen] = useState(false)

    const columns = [
        { field: 'type', headerName: 'Type', flex: 1 },
        { field: 'reason', headerName: 'Reason', flex: 1 },
        { field: 'start', headerName: 'Start', flex: 1 },
        { field: 'end', headerName: 'End', flex: 1 },
        { field: 'internalRemarks', headerName: 'Internal Remarks', flex: 3 },
        {
            field: 'status',
            headerName: 'Status',
            flex: 1,
            renderCell: (params) => {
                const status = params.value
                let bgColor = ''
                let textColor = ''
                let text = ''

                switch (status) {
                    case Status.PENDING:
                        bgColor = 'bg-yellow-100'
                        textColor = 'text-yellow-700'
                        text = 'PENDING'
                        break
                    case Status.APPROVED:
                        bgColor = 'bg-green-100'
                        textColor = 'text-green-700'
                        text = 'APPROVED'
                        break
                    case Status.REJECTED:
                        bgColor = 'bg-red-100'
                        textColor = 'text-red-700'
                        text = 'REJECTED'
                        break
                }

                return <span className={`px-3 py-1 rounded-full font-semibold ${bgColor} ${textColor}`}>{text}</span>
            }
        },
        { field: 'userAssigned', headerName: 'Assigned To', flex: 1 }
    ]

    useEffect(() => {
        fetchEmployeeList(searchTerm, page, pageSize)
    }, [searchTerm, page, pageSize, refresh])

    const fetchEmployeeList = async (search, page, limit) => {
        setLoading(true)
        try {
            const filter = { page, limit, search }
            let response = await fetchLeave(filter)
            const dailyRecords = response.data || []
            setTotalCount(response.count || 0)

            const formattedData = dailyRecords.map((record, index) => ({
                id: record.id || index,
                type: record.type,
                reason: record.reason,
                start: record.start,
                end: record.end,
                status: record.status,
                internalRemarks: record.internalRemarks,
                userAssigned: record.assignedUser
                    ? `${record.assignedUser.firstName} ${record.assignedUser.lastName}`
                    : record.assignedApproverRole
                      ? record.assignedApproverRole
                      : 'N/A'
            }))

            setDTR(formattedData)
        } catch (error) {
            console.error('Error fetching leave data:', error)
        } finally {
            setLoading(false)
        }
    }

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value)
    }

    const handleOpenRegisterDialog = () => {
        setRegisterDialogOpen(true)
    }

    const handleCloseRegisterDialog = () => {
        setRegisterDialogOpen(false)
    }

    const handleRowClick = (params) => {
        navigate(`/leave/details/${params.row.id}`)
    }

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            fetchEmployeeList(searchTerm, page, pageSize)
        }
    }

    const handleLeaveSubmission = async () => {
        setSubmitting(true)
        try {
            setRefresh((prev) => !prev)
        } catch (error) {
            console.error('Error submitting leave:', error)
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div className="p-6 bg-white rounded-lg shadow-lg w-full mx-auto mt-4">
            {loading && <p>Loading...</p>}
            {responseMessage && <p className="text-green-500">{responseMessage}</p>}

            {/* Search and Register Button */}
            <div className="flex justify-center items-center space-x-4 mb-4 w-full">
                <input
                    type="text"
                    placeholder="Search..."
                    className="p-2 border rounded"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onKeyDown={handleKeyDown}
                />

                <button
                    onClick={handleOpenRegisterDialog}
                    className={`px-8 py-2 text-white rounded ${
                        submitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600'
                    }`}
                    disabled={submitting}
                >
                    {submitting ? 'Submitting...' : 'Apply Leave'}
                </button>
            </div>

            <Paper sx={{ width: '100%', height: 'calc(100vh - 250px)', minWidth: 300, overflow: 'hidden' }}>
                <DataGrid
                    rows={dtr}
                    columns={columns}
                    loading={loading}
                    page={page - 1}
                    pageSize={pageSize}
                    rowCount={totalCount}
                    paginationMode="server"
                    initialState={{
                        pagination: { paginationModel: { pageSize: DEFAULT_PAGE_SIZE } }
                    }}
                    pageSizeOptions={[5, 10, 25]}
                    onPaginationModelChange={(newPaginationModel) => {
                        setPage(newPaginationModel.page + 1)
                        setPageSize(newPaginationModel.pageSize)
                    }}
                    onRowSelectionModelChange={(newSelectionModel) => setSelectionModel(newSelectionModel)}
                    selectionModel={selectionModel}
                    onRowClick={handleRowClick}
                    sx={{
                        border: 0,
                        '& .MuiDataGrid-columnHeaders': {
                            fontWeight: 'bold'
                        },
                        '& .MuiDataGrid-cell': {
                            cursor: 'default'
                        }
                    }}
                />
            </Paper>

            <LeaveRegisterModal
                open={registerDialogOpen}
                onClose={handleCloseRegisterDialog}
                onRegisterSuccess={handleLeaveSubmission}
            />
        </div>
    )
}

export default LeaveListLayout
